// lib/supabase.ts — Supabase client for AUTH ONLY (spec §4). Google OAuth.
// Wrapped behind an AuthService interface so components/tests don't depend on supabase-js.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { config } from './env';

export interface AuthUser {
  id: string;
  email?: string;
}

export interface AuthService {
  getUser(): Promise<AuthUser | null>;
  getToken(): Promise<string | null>;
  signInWithGoogle(redirectTo: string): Promise<void>;
  signOut(): Promise<void>;
  onChange(cb: (user: AuthUser | null) => void): () => void;
}

export function createSupabaseAuth(client: SupabaseClient): AuthService {
  return {
    async getUser() {
      const { data } = await client.auth.getUser();
      return data.user ? { id: data.user.id, email: data.user.email ?? undefined } : null;
    },
    async getToken() {
      const { data } = await client.auth.getSession();
      return data.session?.access_token ?? null;
    },
    async signInWithGoogle(redirectTo: string) {
      await client.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } });
    },
    async signOut() {
      await client.auth.signOut();
    },
    onChange(cb) {
      const { data } = client.auth.onAuthStateChange((_event, session) => {
        cb(session?.user ? { id: session.user.id, email: session.user.email ?? undefined } : null);
      });
      return () => data.subscription.unsubscribe();
    },
  };
}

export function createDefaultAuth(): AuthService {
  const client = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });
  return createSupabaseAuth(client);
}
